<template>
  <PaginaAdministrar
    titulo="Arquivos SPED"
    subtitulo="Geração e histórico dos arquivos SPED (EFD ICMS/IPI, EFD Contribuições)"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="abrirGeracao"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Tipo</label>
          <select class="form-select" v-model="filtros.lstTipoSped" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="t in listas.tipoSped" :key="t.idListaClienteItem" :value="t.idListaClienteItem">{{ t.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusGeracao" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.statusGeracao" :key="s.idListaClienteItem" :value="s.idListaClienteItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="arquivos"
      :loading="loading"
      empty-message="Nenhum arquivo SPED gerado."
      row-key="idSpedArquivoGerado"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      @sort-change="handleSortChange"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idSpedArquivoGerado }}</span>
      </template>
      <template #tipo="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle fw-normal">
          {{ getTexto(row.lstTipoSped, listas.tipoSped) }}
        </span>
      </template>
      <template #periodo="{ row }">
        {{ formatarDataBR(row.periodoInicio) }} a {{ formatarDataBR(row.periodoFim) }}
      </template>
      <template #dataGeracao="{ row }">
        {{ formatarDataHoraBR(row.dataGeracao) }}
      </template>
      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getTexto(row.lstStatusGeracao, listas.statusGeracao))">
          {{ getTexto(row.lstStatusGeracao, listas.statusGeracao) }}
        </span>
      </template>
      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon text-primary" title="Baixar arquivo" @click="baixar(row)" :disabled="!row.hashArquivo">
            <i class="bi bi-download"></i>
          </button>
          <button class="btn-icon text-warning" title="Regenerar" @click="regenerar(row)" :disabled="row.lstStatusGeracao === 'SPED_STATUS_GERACAO3' || row.lstStatusGeracao === 'SPED_STATUS_GERACAO2'">
            <i class="bi bi-arrow-clockwise"></i>
          </button>
          <button class="btn-icon text-danger" title="Cancelar" @click="cancelar(row)" :disabled="row.lstStatusGeracao === 'SPED_STATUS_GERACAO3'">
            <i class="bi bi-x-circle"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <SpedsGeracao
    v-if="mostrarGeracao"
    @fechar="mostrarGeracao = false"
    @gerado="onGerado"
    @show-notification="(p) => emit('show-notification', p)"
  />
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { useAuthStore } from '@/stores/auth'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import SpedsGeracao from './SpedsGeracao.vue'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const arquivos = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const mostrarGeracao = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({ tipoSped: [], statusGeracao: [] })
const filtros = reactive({ lstTipoSped: '', lstStatusGeracao: '' })

const colunas = [
  { field: 'idSpedArquivoGerado', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeFilial', label: 'Filial', sortable: false },
  { field: 'lstTipoSped', label: 'Tipo', width: 180, align: 'center', sortable: false, slotName: 'tipo' },
  { field: 'periodoInicio', label: 'Período', width: 200, align: 'center', sortable: false, slotName: 'periodo' },
  { field: 'dataGeracao', label: 'Gerado em', width: 170, align: 'center', sortable: true, slotName: 'dataGeracao' },
  { field: 'nomeContabilista', label: 'Contabilista', sortable: false },
  { field: 'lstStatusGeracao', label: 'Status', width: 130, align: 'center', sortable: false, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 90, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const handleSortChange = () => { buscarDados(1) }

const carregarListas = async () => {
  try {
    const [tipoSped, statusGeracao] = await Promise.all([
      metadataStore.getListaLookup('SPED_TIPO_ARQUIVO', 'Cliente'),
      metadataStore.getListaLookup('SPED_STATUS_GERACAO', 'Cliente')
    ])
    listas.tipoSped = tipoSped || []
    listas.statusGeracao = statusGeracao || []
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getTexto = (codigo, lista) => {
  if (!codigo) return '-'
  const item = (lista || []).find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    arquivos.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (filtros.lstTipoSped) params.append('lstTipoSped', filtros.lstTipoSped)
    if (filtros.lstStatusGeracao) params.append('lstStatusGeracao', filtros.lstStatusGeracao)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/SpedsArquivos?${params.toString()}`)
    arquivos.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error(error)
    erro.value = 'Erro ao carregar arquivos SPED.'
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => buscarDados(1)
const limparFiltros = () => { filtros.lstTipoSped = ''; filtros.lstStatusGeracao = ''; buscarDados(1) }
const mudarPagina = (page) => buscarDados(page)
const mudarTamanhoPagina = (size) => { paginacao.PageSize = size; buscarDados(1) }

const abrirGeracao = () => { mostrarGeracao.value = true }
const onGerado = () => { mostrarGeracao.value = false; buscarDados(1) }

const baixar = async (row) => {
  try {
    const response = await api.get(`/SpedsArquivos/${row.idSpedArquivoGerado}/download`, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `SPED_${row.idFilial}_${row.periodoInicio}_${row.periodoFim}.txt`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao baixar o arquivo.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const regenerar = async (row) => {
  if (!confirm(`Regenerar o arquivo SPED #${row.idSpedArquivoGerado}? O arquivo atual será marcado como "Substituído".`)) return
  try {
    await api.post(`/SpedsArquivos/${row.idSpedArquivoGerado}/regenerar`, {
      idFilial: row.idFilial,
      lstTipoSped: row.lstTipoSped,
      periodoInicio: row.periodoInicio,
      periodoFim: row.periodoFim,
      idContabilista: row.idContabilista,
      observacao: row.observacao
    })
    emit('show-notification', { message: 'Arquivo SPED regenerado com sucesso.', type: 'success' })
    buscarDados(1)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao regenerar o arquivo SPED.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const cancelar = async (row) => {
  if (!confirm(`Cancelar o arquivo SPED #${row.idSpedArquivoGerado}?`)) return
  try {
    await api.post(`/SpedsArquivos/${row.idSpedArquivoGerado}/cancelar`, {})
    emit('show-notification', { message: 'Arquivo SPED cancelado.', type: 'success' })
    buscarDados(1)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao cancelar o arquivo SPED.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else arquivos.value = []
})

onMounted(() => { carregarListas(); buscarDados(1) })
onActivated(() => buscarDados(paginacao.CurrentPage))

defineExpose({ buscarDados })
</script>
